import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LotusIcon from "@/components/icons/LotusIcon";
import { Star, Crown, Diamond, User, Users, Video } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const memberships = [
  {
    title: "Silver Membership",
    icon: Star,
    description: "Access to basic resources, community forum, and monthly group sessions to begin your transformation journey.",
    cta: "Join Silver",
    href: "https://learn.siwongalaxy.com/l/be8ffa5c98",
    isExternal: true,
    accent: "border-muted-foreground/30",
    iconBg: "bg-muted",
    iconColor: "text-muted-foreground",
  },
  {
    title: "Gold Membership",
    icon: Crown,
    description: "All Silver benefits plus monthly group sessions, priority booking, and exclusive content for deeper growth.",
    cta: "Join Gold",
    href: "https://learn.siwongalaxy.com/web/checkout/68f335094b3fd0fcd4aacfe9",
    isExternal: true,
    accent: "border-amber-400/50",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
  },
  {
    title: "Diamond Membership",
    icon: Diamond,
    description: "All Gold benefits plus pre-booked one-on-one sessions, VIP retreats, and personalized guidance for complete transformation.",
    cta: "Join Diamond",
    href: "https://learn.siwongalaxy.com/l/f286241018",
    isExternal: true,
    accent: "border-primary/50",
    iconBg: "bg-primary/20",
    iconColor: "text-primary",
  },
];

const programs = [
  {
    title: "One on One Sessions",
    icon: User,
    description: "Personalized coaching sessions tailored to your unique journey with Vinod Kumar, combining Vedantic wisdom with modern psychology.",
    cta: "Join WhatsApp",
    href: "https://chat.whatsapp.com/Ie7aaYNHDiOGPyZEPvxhfW",
    isExternal: true,
  },
  {
    title: "Workshops",
    icon: Users,
    description: "Interactive group workshops combining Vedantic wisdom with practical exercises for relationship harmony and life transformation.",
    cta: "Join Workshop & Webinar",
    href: "https://learn.siwongalaxy.com/web/checkout/696794e19fa315cfdf89597b",
    isExternal: true,
  },
  {
    title: "Webinars",
    icon: Video,
    description: "Live online sessions exploring key topics in relationship harmony and life transformation through the SIWON methodology.",
    cta: "Join Workshop & Webinar",
    href: "https://learn.siwongalaxy.com/web/checkout/696794e19fa315cfdf89597b",
    isExternal: true,
  },
];

const MembershipPrograms = () => {
  return (
    <div className="min-h-screen" data-testid="membership-page">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary via-teal-dark to-teal-dark mandala-bg relative overflow-hidden">
        <div className="absolute inset-0 mandala-bg opacity-30" />
        <div className="container relative z-10 text-center px-4">
          <div className="max-w-4xl mx-auto animate-fade-up">
            <LotusIcon size={60} className="mx-auto mb-6 text-accent" />
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              Membership & Programs
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/85 max-w-3xl mx-auto leading-relaxed">
              Your Journey to Transformation – Choose from our membership tiers and programs designed 
              to guide you from silence to wonderment.
            </p>
          </div>
        </div>
      </section>

      {/* Memberships Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="text-center mb-12">
            <div className="lotus-divider">
              <LotusIcon size={32} />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
              Memberships
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Choose the membership tier that aligns with your transformation goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {memberships.map((membership) => (
              <Card 
                key={membership.title} 
                className={`card-spiritual border-2 ${membership.accent} hover:shadow-lg transition-all duration-300 flex flex-col`}
              >
                <CardHeader className="text-center">
                  <div className={`w-16 h-16 ${membership.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <membership.icon className={`w-8 h-8 ${membership.iconColor}`} />
                  </div>
                  <CardTitle className="font-serif text-2xl text-primary">
                    {membership.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1">
                  <CardDescription className="text-base leading-relaxed text-center">
                    {membership.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="justify-center pt-4">
                  <Button asChild className="btn-lime">
                    {membership.isExternal ? (
                      <a 
                        href={membership.href} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {membership.cta}
                      </a>
                    ) : (
                      <Link to={membership.href}>{membership.cta}</Link>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-16 bg-sage-dark mandala-bg">
        <div className="container">
          <div className="text-center mb-12">
            <div className="lotus-divider">
              <LotusIcon size={32} />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
              Programs
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Transformative experiences tailored to your unique path of growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {programs.map((program) => (
              <Card 
                key={program.title} 
                className="card-spiritual border border-border hover:border-accent hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <program.icon className="w-8 h-8 text-accent" />
                  </div>
                  <CardTitle className="font-serif text-2xl text-primary">
                    {program.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1">
                  <CardDescription className="text-base leading-relaxed text-center">
                    {program.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="justify-center pt-4">
                  <Button asChild className="btn-lime-outline">
                    {program.isExternal ? (
                      <a 
                        href={program.href} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {program.cta}
                      </a>
                    ) : (
                      <Link to={program.href}>{program.cta}</Link>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer onStartQuiz={() => {}} />
    </div>
  );
};

export default MembershipPrograms;
